import type { ZodType } from 'zod';
import { extractErrors, type FieldError } from '@/lib/excel';

/** The row envelope every wizard Excel parser produces. */
export interface DraftRowShape<TData> {
  data: TData;
  isValid: boolean;
  errors?: FieldError[];
  rowNumber: number;
  /**
   * Present only on rows seeded from an already-imported entity — Weiter uses
   * it to send edits as in-place updates and deletions as surgical removals
   * (ADR 0011, third amendment). Rows added by hand or Excel have none.
   */
  entityId?: string;
}

export type RowValidator<TData> = (data: TData) => { isValid: boolean; errors?: FieldError[] };

export function zodRowValidator<TData>(schemaFactory: () => ZodType<TData>): RowValidator<TData> {
  return (data) => {
    const result = schemaFactory().safeParse(data);
    if (result.success) return { isValid: true, errors: undefined };
    return { isValid: false, errors: extractErrors(result.error) };
  };
}

/** Sequential row numbers after merging or reordering draft batches. */
export function renumberRows<TRow extends { rowNumber: number }>(rows: TRow[]): TRow[] {
  return rows.map((row, index) => ({ ...row, rowNumber: index + 1 }));
}
